import React, { useState, useEffect } from "react";
import { Outlet, Link, useLocation, useNavigate } from "@tanstack/react-router";
import {
  LayoutDashboard,
  Ticket,
  Users,
  BookOpen,
  Building2,
  ChevronLeft,
  Menu,
  LogOut,
  ChevronDown,
  User,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { useIsAdminInAnyOrg } from "@/hooks/useAdminOrganizations";
import type { User as UserType, Customer } from "@/types/auth";
import { Logo } from "./Logo";
import { Breadcrumb } from "./Breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { NotificationBell } from "./notifications";
import { getHelpdeskUrl } from "@/lib/helpdesk-utils";

interface NavItem {
  title: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
}

const getNavigationItems = (isAdminInAnyOrg: boolean): NavItem[] => {
  const baseItems: NavItem[] = [
    {
      title: "Dashboard",
      icon: LayoutDashboard,
      href: getHelpdeskUrl.dashboard(),
    },
    {
      title: "Tickets",
      icon: Ticket,
      href: getHelpdeskUrl.tickets(),
    },
    {
      title: "Customers",
      icon: Users,
      href: getHelpdeskUrl.customers(),
    },
    {
      title: "Knowledge Base",
      icon: BookOpen,
      href: getHelpdeskUrl.knowledgeBase(),
    },
  ];

// Add Org Settings only for users who are admin in at least one organization
  if (isAdminInAnyOrg) {
    baseItems.push({
      title: "Org Settings",
      icon: Building2,
      href: getHelpdeskUrl.orgSettings(),
    });
  }

  return baseItems;
};

const SIDEBAR_COLLAPSED_KEY = "helpdesk.sidebar.collapsed";

const customerPortalNavigationItems: NavItem[] = [
  {
    title: "My Tickets",
    icon: Ticket,
    href: "/customer-portal/tickets",
  },
  {
    title: "Create Ticket",
    icon: LayoutDashboard,
    href: "/customer-portal/tickets/new",
  },
  {
    title: "Knowledge Base",
    icon: BookOpen,
    href: "/customer-portal/knowledge-base",
  },
];

interface AppLayoutProps {
  user?: UserType | null;
  customer?: Customer | null;
  isCustomerPortal?: boolean;
  children?: React.ReactNode;
}

export const AppLayout: React.FC<AppLayoutProps> = ({
  user: propUser,
  customer: propCustomer,
  isCustomerPortal = false,
  children,
}) => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      const stored = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
      return stored === "true";
    } catch {
      return false;
    }
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Persist sidebar state to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(sidebarCollapsed));
    } catch {
      // Ignore localStorage errors (e.g., private browsing)
    }
  }, [sidebarCollapsed]);

  const location = useLocation();
  const navigate = useNavigate();
  const {
    user: authUser,
    customer: authCustomer,
    logout,
  } = useAuth();

  // Check if user is admin in any organization (for showing Org Settings in sidebar)
  const { isAdminInAnyOrg, isLoading: isAdminCheckLoading } = useIsAdminInAnyOrg();

  // Use props if provided, otherwise fall back to auth context
  const user = propUser ?? authUser;
  const customer = propCustomer ?? authCustomer;
  const currentUser = isCustomerPortal ? customer : user;

  // Navigation items for unified helpdesk view (no org slug needed)
  const navItems = isCustomerPortal
    ? customerPortalNavigationItems
    : getNavigationItems(isAdminInAnyOrg);

  const handleLogout = () => {
    logout();
    if (isCustomerPortal) {
      window.location.href = "/customer-portal/login";
    } else {
      navigate({ to: "/" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Mobile menu backdrop */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex flex-col bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700",
          // Prevent content overflow during transition
          "overflow-hidden",
          // Smooth transitions - mobile uses transform, desktop uses width
          "transition-sidebar duration-400 ease-drawer",
          "lg:transition-[width] lg:duration-400 lg:ease-drawer lg:will-change-width",
          // Mobile: slide in from left when open, hidden when closed
          "lg:translate-x-0",
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0",
          // Desktop: normal collapse behavior
          sidebarCollapsed ? "w-64 lg:w-16" : "w-64"
        )}
      >
        {/* Logo Section */}
        <div className={cn(
          "flex h-16 items-center px-4 border-b border-gray-200 dark:border-gray-700 relative",
          sidebarCollapsed ? "justify-center" : "justify-between"
        )}>
          {/* Logo with smooth fade transition - absolute positioning to not affect layout */}
          <div
            className={cn(
              "absolute left-4 transition-opacity duration-150 ease-drawer whitespace-nowrap",
              !sidebarCollapsed
                ? "opacity-100 delay-200"
                : "opacity-0 pointer-events-none"
            )}
          >
            <Link to="/" className="hover:opacity-80 transition-opacity">
              <Logo />
            </Link>
          </div>
          {/* Desktop-only collapse button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={cn(
              "hidden lg:flex",
              !sidebarCollapsed && "ml-auto"
            )}
          >
            {sidebarCollapsed ? (
              <Menu className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </Button>
          {/* Mobile close button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(false)}
            className="ml-auto lg:hidden"
          >
            <ChevronLeft className="h-5 w-5" />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 space-y-1 px-2 py-4">
          {isAdminCheckLoading && !isCustomerPortal ? (
            // Skeleton placeholders while loading admin check
            <>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="flex items-center px-3 py-2 rounded-lg"
                >
                  <div className={cn(
                    "h-5 w-5 bg-gray-200 dark:bg-gray-700 rounded animate-pulse flex-shrink-0",
                    !sidebarCollapsed && "mr-3"
                  )} />
                  {!sidebarCollapsed && (
                    <div className="h-4 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
                  )}
                </div>
              ))}
            </>
          ) : (
            navItems.map((item) => {
              // Improved active detection for org-specific routes
              const isActive =
                location.pathname === item.href ||
                (location.pathname.startsWith(item.href + "/") &&
                  item.href !== "/");
              return (
                <Link
                  key={item.href}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={cn(
                    "flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  )}
                >
                  <div className="flex items-center">
                    <item.icon
                      className={cn(
                        "h-5 w-5 flex-shrink-0",
                        !sidebarCollapsed && "mr-3"
                      )}
                    />
                    {/* Text with smooth fade transition */}
                    <span
                      className={cn(
                        "whitespace-nowrap transition-opacity duration-150 ease-drawer",
                        !sidebarCollapsed
                          ? "opacity-100 delay-200"
                          : "opacity-0 pointer-events-none"
                      )}
                    >
                      {item.title}
                    </span>
                  </div>
                  {/* Badge with smooth fade transition */}
                  {item.badge && (
                    <Badge
                      variant="secondary"
                      className={cn(
                        "ml-auto transition-opacity duration-150 ease-drawer",
                        !sidebarCollapsed
                          ? "opacity-100 delay-200"
                          : "opacity-0 pointer-events-none"
                      )}
                    >
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              );
            })
          )}
        </nav>

      </aside>

      {/* Main Content */}
      <div
        className={cn(
          // Smooth margin transition for main content - matches sidebar duration
          "transition-spacing duration-400 ease-drawer",
          // Mobile: no margin (sidebar is overlay)
          // Desktop: margin based on sidebar state
          "lg:ml-64",
          sidebarCollapsed && "lg:ml-16"
        )}
      >
        {/* Header */}
        <header className="sticky top-0 z-40 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex h-16 items-center justify-between px-3 sm:px-4 md:px-6">
            <div className="flex items-center flex-1 gap-2 sm:gap-3">
              {/* Mobile menu button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden"
              >
                <Menu className="h-5 w-5" />
              </Button>

              <div className="min-w-0 flex-1">
                <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white truncate">
                  {navItems.find((item) =>
                    location.pathname.startsWith(item.href)
                  )?.title ||
                    (isCustomerPortal ? "Customer Portal" : "EU ServiceLine")}
                </h1>
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2 md:gap-4">
              {/* Notifications - Only show for agents/admins in helpdesk portal */}
              {!isCustomerPortal && <NotificationBell />}

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-1 md:gap-2 px-0 min-[400px]:px-2 md:px-4">
                    <div className="h-8 w-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-sm font-medium flex-shrink-0">
                      {currentUser?.firstName?.[0]}
                      {currentUser?.lastName?.[0]}
                    </div>
                    <ChevronDown className="h-4 w-4 hidden min-[400px]:inline" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="font-normal">
                    <div className="flex flex-col space-y-1">
                      <p className="text-sm font-medium leading-none">
                        {currentUser?.fullName ||
                          `${currentUser?.firstName} ${currentUser?.lastName}`}
                      </p>
                      <p className="text-xs leading-none text-muted-foreground">
                        {currentUser?.email}
                      </p>
                    </div>
                  </DropdownMenuLabel>

                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() =>
                      navigate({ to: getHelpdeskUrl.profile() })
                    }
                    className="cursor-pointer"
                  >
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={handleLogout}
                    className="cursor-pointer"
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6">
          {!isCustomerPortal && (
            <div className="hidden md:block">
              <Breadcrumb />
            </div>
          )}
          {children || <Outlet />}
        </main>

        {/* Footer */}
        {!isCustomerPortal && (
          <footer className="border-t border-gray-200 dark:border-gray-700 py-4 px-4 md:px-6">
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              &copy; {new Date().getFullYear()} EU ServiceLine. All rights reserved.
            </p>
          </footer>
        )}
      </div>
    </div>
  );
};
